/**
 * BattleScreen Component - Full screen battle view with builds display and dual team export
 */

import { useState } from "react";
import { useApp } from "../context/AppContext";
import { formatTeamForShowdown, formatBothTeams, copyToClipboard, openShowdown } from "../utils/showdown";
import { getBuildForPokemon, SmogonSet } from "../data/smogonSets";
import { translateType, getTypeColor } from "../utils/typeUtils";
import { showToast } from "./Toast";
import { Tier, Pokemon, SavedTeam } from "../types/pokemon";

const TIERS: { value: Tier; label: string }[] = [
  { value: "all", label: "Tous" },
  { value: "ou", label: "OU" },
  { value: "uu", label: "UU" },
  { value: "ag", label: "AG" },
];

export function BattleScreen() {
  const { state, dispatch } = useApp();
  const team1 = state.teams[0].pokemon;
  const team2 = state.teams[1].pokemon;
  const [selectedTier, setSelectedTier] = useState<Tier>(state.settings.tier);

  const handleCopyTeam1 = async () => {
    const text = formatTeamForShowdown(team1, selectedTier);
    await copyToClipboard(text);
    showToast("Equipe J1 copiee !", "success");
  };

  const handleCopyTeam2 = async () => {
    const text = formatTeamForShowdown(team2, selectedTier);
    await copyToClipboard(text);
    showToast("Equipe J2 copiee !", "success");
  };

  const handleCopyBoth = async () => {
    const text = formatBothTeams(team1, team2, selectedTier);
    await copyToClipboard(text);
    showToast("Les 2 equipes copiees !", "success");
  };

  const handleLaunchShowdown = async () => {
    const text = formatTeamForShowdown(team1, selectedTier);
    await copyToClipboard(text);
    openShowdown();
    showToast("Equipe J1 copiee ! Collez-la dans le teambuilder.", "info");
  };

  const handleSaveTeam = (playerNumber: 1 | 2) => {
    const team = playerNumber === 1 ? team1 : team2;
    const defaultName = `Equipe J${playerNumber} - ${new Date().toLocaleDateString("fr-FR")}`;
    const name = window.prompt("Nom de l'equipe :", defaultName);
    if (!name) return;

    const savedTeam: SavedTeam = {
      id: String(Date.now()),
      name,
      team,
      date: new Date().toLocaleDateString("fr-FR"),
    };
    dispatch({ type: "SAVE_TEAM", team: savedTeam });
    showToast(`Equipe "${name}" sauvegardee !`, "success");
  };

  const handleNewGame = () => {
    dispatch({ type: "RESET_DRAFT" });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-y-auto">
      {/* Background effects */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
      </div>

      {/* Tier selector */}
      <div className="relative z-10 flex items-center gap-3 mt-6 mb-2">
        <span className="text-white/70 font-bold text-sm uppercase tracking-wider">Tier :</span>
        {TIERS.map((t) => (
          <button
            key={t.value}
            onClick={() => setSelectedTier(t.value)}
            className={`px-5 py-2 rounded-lg font-bold text-sm uppercase tracking-wider transition-all duration-200 border-2 ${
              selectedTier === t.value
                ? "bg-yellow-400 text-gray-900 border-yellow-500 shadow-lg shadow-yellow-400/30"
                : "bg-gray-700 text-white/70 border-gray-600 hover:bg-gray-600 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Main battle area */}
      <div className="relative z-10 flex items-start justify-center gap-8 w-full max-w-7xl px-8 py-4 flex-1">
        {/* Team 1 (Blue) */}
        <div className="flex-1 max-w-lg">
          <TeamDisplay team={team1} color="blue" playerNumber={1} tier={selectedTier} />
        </div>

        {/* VS Section */}
        <div className="flex flex-col items-center gap-6 pt-16">
          <div
            className="text-8xl font-black text-yellow-400"
            style={{
              textShadow: `
                4px 4px 0 rgba(0, 0, 0, 0.5),
                -2px -2px 0 rgba(255, 255, 255, 0.3),
                0 0 40px rgba(255, 222, 0, 0.8),
                0 0 80px rgba(255, 222, 0, 0.4)
              `,
              fontFamily: "Impact, sans-serif",
              letterSpacing: "0.1em",
            }}
          >
            VS
          </div>
        </div>

        {/* Team 2 (Red) */}
        <div className="flex-1 max-w-lg">
          <TeamDisplay team={team2} color="red" playerNumber={2} tier={selectedTier} />
        </div>
      </div>

      {/* Export buttons */}
      <div className="relative z-10 flex flex-wrap items-center justify-center gap-3 pb-3">
        <button
          onClick={handleCopyTeam1}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-2.5 rounded-xl border-2 border-blue-400 transition-all duration-200 hover:scale-105 text-sm"
        >
          Copier Equipe J1
        </button>
        <button
          onClick={handleCopyTeam2}
          className="bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-2.5 rounded-xl border-2 border-red-400 transition-all duration-200 hover:scale-105 text-sm"
        >
          Copier Equipe J2
        </button>
        <button
          onClick={handleCopyBoth}
          className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-5 py-2.5 rounded-xl border-2 border-purple-400 transition-all duration-200 hover:scale-105 text-sm"
        >
          Copier les 2
        </button>
        <button
          onClick={handleLaunchShowdown}
          className="bg-gradient-to-b from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 font-black px-6 py-2.5 rounded-xl border-2 border-yellow-600 transition-all duration-200 hover:scale-105 text-sm uppercase tracking-wider shadow-lg"
        >
          Lancer sur Showdown
        </button>
        <button
          onClick={() => handleSaveTeam(1)}
          className="bg-green-600 hover:bg-green-700 text-white font-bold px-5 py-2.5 rounded-xl border-2 border-green-400 transition-all duration-200 hover:scale-105 text-sm"
        >
          Sauvegarder J1
        </button>
        <button
          onClick={() => handleSaveTeam(2)}
          className="bg-green-600 hover:bg-green-700 text-white font-bold px-5 py-2.5 rounded-xl border-2 border-green-400 transition-all duration-200 hover:scale-105 text-sm"
        >
          Sauvegarder J2
        </button>
        <button
          onClick={handleNewGame}
          className="bg-gray-600 hover:bg-gray-700 text-white font-bold px-5 py-2.5 rounded-xl border-2 border-gray-500 transition-all duration-200 hover:scale-105 text-sm"
        >
          Nouvelle partie
        </button>
      </div>

      {/* Instructions */}
      <div className="relative z-10 pb-6 text-center text-white/50 text-xs max-w-xl px-4 space-y-1">
        <p><strong>Joueur 1 :</strong> Cliquez sur &quot;Copier Equipe J1&quot; puis collez dans le teambuilder Showdown.</p>
        <p><strong>Joueur 2 :</strong> Cliquez sur &quot;Copier Equipe J2&quot; puis collez dans le teambuilder Showdown.</p>
        <p>Selectionnez le tier pour adapter les builds affiches et exportes.</p>
      </div>
    </div>
  );
}

// --- Team Display ---

interface TeamDisplayProps {
  team: Pokemon[];
  color: "blue" | "red";
  playerNumber: number;
  tier: Tier;
}

function TeamDisplay({ team, color, playerNumber, tier }: TeamDisplayProps) {
  const bgGradient = color === "blue" ? "from-blue-600 to-blue-800" : "from-red-600 to-red-800";
  const borderColor = color === "blue" ? "border-blue-400" : "border-red-400";
  const glowColor = color === "blue" ? "rgba(59, 130, 246, 0.5)" : "rgba(239, 68, 68, 0.5)";

  return (
    <div
      className={`bg-gradient-to-b ${bgGradient} ${borderColor} border-4 rounded-3xl p-6 shadow-2xl`}
      style={{
        boxShadow: `
          0 0 40px ${glowColor},
          0 20px 40px rgba(0, 0, 0, 0.5),
          inset 0 2px 0 rgba(255, 255, 255, 0.2)
        `,
        backdropFilter: "blur(10px)",
      }}
    >
      {/* Player Title */}
      <div className="text-center mb-4">
        <h2
          className="text-white font-black text-2xl uppercase tracking-wider mb-1"
          style={{ textShadow: "3px 3px 0 rgba(0, 0, 0, 0.5)" }}
        >
          Joueur {playerNumber}
        </h2>
        <div className="text-yellow-300 font-retro text-sm">
          {team.length}/6 Pokemon
        </div>
      </div>

      {/* Team Grid */}
      <div className="grid grid-cols-3 gap-3">
        {team.map((pokemon) => (
          <BattlePokemonCard key={pokemon.id} pokemon={pokemon} tier={tier} />
        ))}
      </div>
    </div>
  );
}

// --- Pokemon Card with hover build ---

interface BattlePokemonCardProps {
  pokemon: Pokemon;
  tier: Tier;
}

function BattlePokemonCard({ pokemon, tier }: BattlePokemonCardProps) {
  const [hovered, setHovered] = useState(false);
  const build: SmogonSet = getBuildForPokemon(pokemon, tier);

  return (
    <div
      className="relative bg-white/95 rounded-xl p-3 shadow-lg transform transition-all hover:scale-110 cursor-pointer group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Pokemon Image */}
      <div className="w-full aspect-square mb-2 flex items-center justify-center">
        <img
          src={pokemon.sprite}
          alt={pokemon.nameFr}
          className="w-full h-full object-contain"
          style={{ imageRendering: "pixelated" }}
        />
      </div>

      {/* Pokemon Name */}
      <div className="text-center">
        <p className="font-retro text-[10px] text-gray-800 uppercase truncate">
          {pokemon.nameFr}
        </p>
        <p className="text-[8px] text-gray-600">
          #{String(pokemon.id).padStart(3, "0")}
        </p>
      </div>

      {/* Types */}
      <div className="flex gap-1 justify-center mt-2">
        {pokemon.types.map((type) => (
          <div
            key={type}
            className="px-2 py-0.5 rounded text-white text-[8px] font-bold uppercase"
            style={{ backgroundColor: getTypeColor(type) }}
          >
            {translateType(type)}
          </div>
        ))}
      </div>

      {/* Build tooltip on hover */}
      {hovered && (
        <div className="absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-gray-900/95 border border-gray-600 rounded-lg p-3 shadow-2xl text-left pointer-events-none">
          <div className="text-yellow-400 font-bold text-xs mb-1 truncate">{build.name}</div>
          {build.item && (
            <div className="text-white/80 text-[10px]">
              <span className="text-gray-400">Objet :</span> {build.item}
            </div>
          )}
          {build.ability && (
            <div className="text-white/80 text-[10px]">
              <span className="text-gray-400">Talent :</span> {build.ability}
            </div>
          )}
          {build.nature && (
            <div className="text-white/80 text-[10px]">
              <span className="text-gray-400">Nature :</span> {build.nature}
            </div>
          )}
          <div className="mt-1 border-t border-gray-700 pt-1">
            <div className="text-gray-400 text-[10px] mb-0.5">Moves :</div>
            {build.moves.map((move, i) =>
              move ? (
                <div key={i} className="text-white/90 text-[10px] pl-1">
                  - {move}
                </div>
              ) : null
            )}
          </div>
          {/* Arrow */}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-gray-900/95" />
        </div>
      )}
    </div>
  );
}

export default BattleScreen;
